import * as bootstrap from 'bootstrap';

// The desktop Product panel (#nav-product-menu) opens from the Product nav link on
// hover or click and stays while either is hovered or focused. `d-none` comes off
// a frame before `show` so the opacity transition runs, and goes back on only once
// the fade-out has ended without a re-open.
function initializeProductMenu() {
    const $menu = document.querySelector<HTMLElement>('#nav-product-menu');
    const $links = Array.from(document.querySelectorAll<HTMLElement>('.nav-link-product'));
    if (!$menu || !$links.length) {
        return;
    }

    let hideTimeout = 0;

    const show = () => {
        clearTimeout(hideTimeout);
        $menu.classList.remove('d-none');
        requestAnimationFrame(() => $menu.classList.add('show'));
        $links.forEach($link => $link.setAttribute('aria-expanded', 'true'));
    };

    const hide = () => {
        clearTimeout(hideTimeout);
        if (!$menu.classList.contains('show')) {
            return;
        }
        $menu.classList.remove('show');
        $links.forEach($link => $link.setAttribute('aria-expanded', 'false'));
        $menu.addEventListener('transitionend', () => {
            if (!$menu.classList.contains('show')) {
                $menu.classList.add('d-none');
            }
        }, { once: true });
    };

    const startHideTimer = () => {
        clearTimeout(hideTimeout);
        hideTimeout = window.setTimeout(hide, 300);
    };

    for (const $link of $links) {
        $link.addEventListener('mouseenter', show);
        $link.addEventListener('mouseleave', startHideTimer);
        $link.addEventListener('click', e => {
            e.preventDefault();
            if ($menu.classList.contains('show')) {
                hide();
            } else {
                show();
            }
        });
    }

    $menu.addEventListener('mouseenter', show);
    $menu.addEventListener('mouseleave', startHideTimer);

    // Keyboard users tab from the link into the panel; leaving both closes it. A
    // click on something unfocusable inside the panel (the switch track) blurs the
    // old target with no relatedTarget, so the check waits for focus to land.
    $menu.addEventListener('focusout', () => {
        window.setTimeout(() => {
            const $active = document.activeElement;
            if ($active && $active !== document.body && ($menu.contains($active) || $links.some($link => $link.contains($active)))) {
                return;
            }
            if ($menu.matches(':hover')) {
                return;
            }
            hide();
        });
    });

    document.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
            hide();
        }
    });

    document.addEventListener('click', e => {
        const $target = e.target as Node;
        if ($menu.contains($target) || $links.some($link => $link.contains($target))) {
            return;
        }
        hide();
    });

    // The panel is fixed under the header, whose height changes with banners and
    // breakpoints.
    const $header = document.querySelector<HTMLElement>('header');
    if ($header) {
        new ResizeObserver(entries => {
            for (const entry of entries) {
                $menu.style.setProperty('--header-height', `${(entry.target as HTMLElement).offsetHeight}px`);
            }
        }).observe($header);
    }
}

// The By Uses / By Types labels are Bootstrap tabs; the track between them is
// decorative, so a click on it picks whichever set isn't showing.
function initializeMenuSwitches() {
    document.querySelectorAll<HTMLElement>('.nav-menu-switch-track').forEach($track => {
        $track.addEventListener('click', () => {
            const $tab = $track.closest('[role="tablist"]')?.querySelector<HTMLElement>('[role="tab"]:not(.active)');
            if ($tab) {
                bootstrap.Tab.getOrCreateInstance($tab).show();
            }
        });
    });
}

// Below xl the offcanvas drills from the link list into the Product view and back,
// and reopens on the list.
function initializeMobileDrill() {
    const $offcanvas = document.querySelector<HTMLElement>('#nav-offcanvas');
    const $main = document.querySelector<HTMLElement>('#mobile-nav-main');
    const $product = document.querySelector<HTMLElement>('#mobile-nav-product');
    if (!$offcanvas || !$main || !$product) {
        return;
    }

    const showProduct = (drilled: boolean) => {
        $main.classList.toggle('d-none', drilled);
        $product.classList.toggle('d-none', !drilled);
    };

    $main.querySelectorAll('.nav-menu-drill').forEach($drill => {
        $drill.addEventListener('click', () => {
            showProduct(true);
            $product.querySelector<HTMLElement>('.nav-menu-back')?.focus();
        });
    });
    $product.querySelectorAll('.nav-menu-back').forEach($back => {
        $back.addEventListener('click', () => {
            showProduct(false);
            $main.querySelector<HTMLElement>('.nav-menu-drill')?.focus();
        });
    });
    $offcanvas.addEventListener('hidden.bs.offcanvas', () => showProduct(false));
}

document.addEventListener('DOMContentLoaded', () => {
    initializeProductMenu();
    initializeMenuSwitches();
    initializeMobileDrill();
});
